// Concept primitives, per design/03_Rune_Language.md.
// Mass/Space/Time/Charge are the true dimensional primitives; Energy and Momentum
// are named first-class runes derived from them (kept separate here for display and
// cost purposes, but their `dimension` field shows the underlying composition).
// Entropy is a fifth primitive of a different kind: a measure over configurations of
// the other four, not a magnitude built from them, so it has no dimension vector.

export interface Dimension {
  mass: number;
  space: number;
  time: number;
  charge: number;
}

export type ConceptId =
  | 'mass'
  | 'space'
  | 'time'
  | 'charge'
  | 'entropy'
  | 'energy'
  | 'momentum';

export interface Concept {
  id: ConceptId;
  name: string;
  kind: 'primitive' | 'named-derived';
  /** null only for Entropy, which is not a product of the other dimensions. */
  dimension: Dimension | null;
  isEntropy: boolean;
  /** Rough relative cost multiplier for manipulating this concept directly. */
  baseCost: number;
  description: string;
  status?: string;
}

const dim = (mass: number, space: number, time: number, charge: number): Dimension => ({
  mass,
  space,
  time,
  charge,
});

export const CONCEPTS: Record<ConceptId, Concept> = {
  mass: {
    id: 'mass',
    name: 'Mass',
    kind: 'primitive',
    dimension: dim(1, 0, 0, 0),
    isEntropy: false,
    baseCost: 5,
    description: 'True primitive. Quantity of matter at a locus.',
  },
  space: {
    id: 'space',
    name: 'Space/Distance',
    kind: 'primitive',
    dimension: dim(0, 1, 0, 0),
    isEntropy: false,
    baseCost: 3,
    description: 'True primitive. Position/extent — Distance and Position are both just usages of this dimension.',
  },
  time: {
    id: 'time',
    name: 'Time',
    kind: 'primitive',
    dimension: dim(0, 0, 1, 0),
    isEntropy: false,
    baseCost: 50,
    description:
      'True primitive. Manipulating its local rate is disproportionately expensive — real time dilation needs relativistic velocity or gravitational field strength.',
  },
  charge: {
    id: 'charge',
    name: 'Charge',
    kind: 'primitive',
    dimension: dim(0, 0, 0, 1),
    isEntropy: false,
    baseCost: 4,
    description: 'True primitive, independent of Mass/Space/Time. The source term behind electromagnetic Fields.',
  },
  entropy: {
    id: 'entropy',
    name: 'Entropy',
    kind: 'primitive',
    dimension: null,
    isEntropy: true,
    baseCost: 1,
    description:
      'A different kind of primitive: a measure over arrangements of the other four, not a magnitude built from them. Monotonic, not conserved — see the increase/decrease asymmetry below.',
  },
  energy: {
    id: 'energy',
    name: 'Energy',
    kind: 'named-derived',
    dimension: dim(1, 2, -2, 0),
    isEntropy: false,
    baseCost: 1,
    description: 'Named first-class rune. Mass·Space²/Time² under the hood — the SI-Joule-style shorthand.',
  },
  momentum: {
    id: 'momentum',
    name: 'Momentum',
    kind: 'named-derived',
    dimension: dim(1, 1, -1, 0),
    isEntropy: false,
    baseCost: 2,
    description: 'Named first-class rune, tentative (see 07_Open_Questions.md). Mass·Space/Time under the hood.',
    status: 'open question — treated as confirmed for this prototype',
  },
};

// Display/progression order, not definition order: Energy first (the earliest,
// cheapest, most intuitive concept to manipulate); Entropy/Time/Mass last, since each
// is disproportionately expensive or dangerous to manipulate directly (see
// design/04_Physics_Engine.md) and are intended as mid-to-late-game concepts.
const CONCEPT_ORDER: ConceptId[] = ['energy', 'space', 'charge', 'momentum', 'entropy', 'time', 'mass'];

export const CONCEPT_LIST: Concept[] = CONCEPT_ORDER.map((id) => CONCEPTS[id]);

export function formatDimension(dimension: Dimension | null): string {
  if (dimension === null) return 'dimensionless (count of microstates)';
  const parts: string[] = [];
  const sup = (n: number) =>
    n === 1 ? '' : n.toString().replace('-', '⁻').replace(/\d/g, (d) => '⁰¹²³⁴⁵⁶⁷⁸⁹'[Number(d)]);
  if (dimension.mass) parts.push(`M${sup(dimension.mass)}`);
  if (dimension.space) parts.push(`L${sup(dimension.space)}`);
  if (dimension.time) parts.push(`T${sup(dimension.time)}`);
  if (dimension.charge) parts.push(`Q${sup(dimension.charge)}`);
  return parts.length ? parts.join('·') : 'dimensionless';
}
